/**
 * Keeps the cart drawer, cart counter, and Shopify cart in one state.
 * This deliberately uses Shopify's Ajax Cart API instead of storing a second
 * cart state in the browser, so add, remove, and quantity changes stay in sync.
 */
(() => {
  'use strict';

  const sectionNames = 'cart-drawer,cart-icon-bubble';

  // Shopify's Liquid routes are returned without the `.js` suffix (for
  // example `/cart/add`).  The Ajax Cart API needs the JSON endpoints, so
  // normalize both configured and fallback routes before every request.
  // Keeping the configured path preserves locale/market prefixes such as
  // `/en/cart/add`.
  const ajaxRoute = (route, fallback) => {
    const value = route || fallback;
    return value.endsWith('.js') ? value : `${value.replace(/\/$/, '')}.js`;
  };

  const cartAddUrl = () => ajaxRoute(window.routes?.cart_add_url, '/cart/add');
  const cartChangeUrl = () => ajaxRoute(window.routes?.cart_change_url, '/cart/change');
  const drawerSelector = 'cart-drawer';
  const changeTimers = new Map();

  const setStatus = (message, isError = false) => {
    const drawer = document.querySelector(drawerSelector);
    const status = drawer?.querySelector('[data-kanaan-cart-status]');
    if (!status) return;

    status.textContent = message || '';
    status.hidden = !message;
    status.classList.toggle('kanaan-cart-status--error', isError);
    status.classList.toggle('kanaan-cart-status--success', !isError && Boolean(message));
  };

  const setFormError = (form, message) => {
    const productForm = form.closest('product-form');
    const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
    const messageElement = wrapper?.querySelector('.product-form__error-message');

    if (wrapper && messageElement) {
      messageElement.textContent = message;
      wrapper.hidden = false;
    }

    setStatus(message, true);
  };

  const clearFormError = (form) => {
    const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    if (wrapper) wrapper.hidden = true;
    setStatus('');
  };

  const setButtonLoading = (button, loading) => {
    if (!button) return;

    if (loading) {
      button.dataset.kanaanWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('kanaan-cart-is-loading');
      return;
    }

    if (button.dataset.kanaanWasDisabled !== 'true') button.disabled = false;
    delete button.dataset.kanaanWasDisabled;
    button.removeAttribute('aria-busy');
    button.classList.remove('kanaan-cart-is-loading');
  };

  const parseError = async (response) => {
    try {
      const data = await response.json();
      const detail = data.description || data.message || data.errors;
      if (typeof detail === 'string') return detail;
      if (detail && typeof detail === 'object') return Object.values(detail).flat().join(' ');
      return 'Unable to update your bag. Please try again.';
    } catch (_) {
      return 'Unable to update your bag. Please try again.';
    }
  };

  const parseSection = (markup) => new DOMParser().parseFromString(markup, 'text/html');

  const getSelectedQuantity = (form) =>
    Array.from(document.querySelectorAll('input[data-kanaan-ritual-choice]')).find(
      (input) => input.form === form && input.checked
    );

  const trustedVariantId = (form, submittedId) => {
    const variantInput = form.querySelector('input[name="id"][data-kanaan-variant-ids]');
    if (!variantInput) return submittedId;

    const allowedIds = (variantInput.dataset.kanaanVariantIds || '').split(',').filter(Boolean);
    if (allowedIds.includes(submittedId)) return submittedId;

    // A foreign id can be injected by an older product-form listener when a
    // page contains more than one product CTA. Restore this product's server
    // rendered variant before making an Ajax request.
    const fallbackId = variantInput.dataset.kanaanFallbackVariantId || allowedIds[0] || '';
    if (fallbackId) variantInput.value = fallbackId;
    return fallbackId;
  };

  const formDataWithSelectedQuantity = (form) => {
    const data = new FormData(form);
    const selectedQuantity = getSelectedQuantity(form);
    const variantId = trustedVariantId(form, String(data.get('id') || ''));

    if (selectedQuantity) data.set('quantity', selectedQuantity.value);
    if (variantId) data.set('id', variantId);
    return data;
  };

  const drawerSections = (drawer) =>
    drawer?.getSectionsToRender?.().map((section) => section.id) || [];

  // The theme's original product form and the custom cart drawer both listen
  // for the same submit event. Centralising the successful add flow here
  // prevents an item from being added without opening the drawer.
  const openDrawer = () => {
    const drawer = document.querySelector(drawerSelector);
    if (!drawer) return;

    drawer.classList.remove('is-empty');
    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    if (typeof window.openCartDrawer === 'function') {
      window.openCartDrawer();
      return;
    }

    drawer.classList.add('active', 'animate');
  };

  const renderAndOpenDrawer = async (state, drawer) => {
    if (state.sections && drawer?.renderContents) {
      drawer.renderContents(state);
    } else {
      await renderCartSections();
    }

    // Allow the cart section to finish replacing its markup before focus is
    // moved into the drawer.
    window.requestAnimationFrame(openDrawer);
  };

  const renderCartSections = async () => {
    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.set('sections', sectionNames);

    const response = await fetch(requestUrl.toString(), {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error('Unable to refresh the cart.');
    const sections = await response.json();

    const existingDrawer = document.querySelector(drawerSelector);
    if (existingDrawer?.renderContents && sections['cart-drawer'] && sections['cart-icon-bubble']) {
      existingDrawer.renderContents({ id: null, sections });
      return;
    }

    if (sections['cart-drawer']) {
      const replacementDrawer = parseSection(sections['cart-drawer']).querySelector(drawerSelector);
      if (existingDrawer && replacementDrawer) {
        existingDrawer.replaceWith(replacementDrawer);
        replacementDrawer.open?.();
      }
    }

    if (sections['cart-icon-bubble']) {
      const cartBubble = document.getElementById('cart-icon-bubble');
      if (cartBubble) cartBubble.innerHTML = sections['cart-icon-bubble'];
    }

    document.dispatchEvent(new CustomEvent('kanaan:cart-updated'));
  };

  const directCheckout = async (form, button) => {
    clearFormError(form);
    setButtonLoading(button, true);

    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formDataWithSelectedQuantity(form),
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(await parseError(response));

      window.location.assign('/checkout');
    } catch (error) {
      setFormError(form, error.message || 'Unable to start checkout. Please try again.');
    } finally {
      setButtonLoading(button, false);
    }
  };

  const addProductToCart = async (form, button) => {
    if (form.dataset.kanaanCartPending === 'true') return;

    const drawer = document.querySelector(drawerSelector);
    form.dataset.kanaanCartPending = 'true';
    clearFormError(form);
    setButtonLoading(button, true);

    try {
      const formData = formDataWithSelectedQuantity(form);
      const variantId = String(formData.get('id') || '');

      if (!/^\d+$/.test(variantId)) {
        throw new Error('Please select an available product option.');
      }

      const sections = drawerSections(drawer);
      if (sections.length) {
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
        drawer?.setActiveElement?.(document.activeElement);
      }

      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(await parseError(response));
      const state = await response.json();
      if (state?.status) {
        throw new Error(state.description || state.message || 'Unable to add this item. Please try again.');
      }

      await renderAndOpenDrawer(state, drawer);
      setStatus('Added to your ritual bag.');
    } catch (error) {
      setFormError(form, error.message || 'Unable to add this item. Please try again.');
    } finally {
      delete form.dataset.kanaanCartPending;
      setButtonLoading(button, false);
    }
  };

  const addCartUpsell = async (form, button) => {
    const drawer = document.querySelector(drawerSelector);
    const upsell = form.closest('[data-kanaan-upsell-variant-id]');
    const variantId = upsell?.dataset.kanaanUpsellVariantId;
    if (!drawer) return;

    setButtonLoading(button, true);
    setStatus('Adding your ritual essential…');

    try {
      const formData = new FormData(form);
      if (!variantId || !/^\d+$/.test(variantId)) {
        throw new Error('This offer is not linked to a purchasable variant. Please refresh and try again.');
      }

      // The card's server-rendered available variant is the source of truth.
      // Set it explicitly so a stale or duplicated form field cannot add a different variant.
      formData.set('id', variantId);
      formData.set('quantity', '1');
      const sections = drawerSections(drawer);

      if (sections.length) {
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);
        drawer.setActiveElement?.(document.activeElement);
      }

      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        credentials: 'same-origin',
      });
      // A successful `/cart/add.js` response is JSON. Parse it only after
      // checking the status so a non-JSON error page cannot replace
      // Shopify's useful error payload with a generic JSON parse error.
      if (!response.ok) throw new Error(await parseError(response));
      const state = await response.json();

      if (!response.ok || state?.status) {
        throw new Error(state?.description || state?.message || 'Unable to add this item. Please try again.');
      }

      await renderAndOpenDrawer(state, drawer);
      setStatus('Added to your ritual bag.');
    } catch (error) {
      setStatus(error.message || 'Unable to add this item. Please try again.', true);
    } finally {
      setButtonLoading(button, false);
    }
  };

  const changeCart = async (line, quantity, source) => {
    const drawer = document.querySelector(drawerSelector);
    if (!drawer || !line) return;

    drawer.setAttribute('aria-busy', 'true');
    setStatus('Updating your ritual bag…');

    try {
      const response = await fetch(cartChangeUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
          sections: drawerSections(drawer),
          sections_url: window.location.pathname,
        }),
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(await parseError(response));
      const state = await response.json();
      await renderAndOpenDrawer(state, drawer);
      setStatus('Your ritual bag is updated.');
    } catch (error) {
      setStatus(error.message || 'Unable to update your bag. Please try again.', true);
      if (source?.matches?.('.quantity__input')) source.dispatchEvent(new Event('change', { bubbles: false }));
    } finally {
      document.querySelector(drawerSelector)?.removeAttribute('aria-busy');
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const directCheckoutButton = event.target.closest('[data-kanaan-direct-checkout]');
      if (directCheckoutButton) {
        const form = document.getElementById(directCheckoutButton.dataset.formId);
        if (!form) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        directCheckout(form, directCheckoutButton);
        return;
      }

      const removeControl = event.target.closest('cart-remove-button');
      if (!removeControl || !removeControl.closest(drawerSelector)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      changeCart(removeControl.dataset.index, 0, removeControl);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('cart-drawer .quantity__input')) return;

      event.stopImmediatePropagation();
      const line = input.dataset.index;
      if (!line) return;

      window.clearTimeout(changeTimers.get(line));
      changeTimers.set(
        line,
        window.setTimeout(() => changeCart(line, input.value, input), 120)
      );
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (form.matches('[data-kanaan-cart-upsell-form]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        addCartUpsell(form, event.submitter || form.querySelector('[data-kanaan-cart-upsell-add]'));
        return;
      }

      // Handle every regular product Add to Cart button, including the sticky
      // product button and the homepage featured-product fallback. `skip_cart`
      // is a legacy theme setting; the store's visible Add to Cart buttons must
      // always use the Ajax Cart API and open the drawer. Direct checkout has
      // its own dedicated button and handler above.
      if (!form.matches('[data-type="add-to-cart-form"]')) return;

      // Do not take over accelerated-payment buttons that Shopify may insert
      // into the product form; they keep their own direct-checkout behaviour.
      if (
        event.submitter instanceof HTMLElement &&
        !event.submitter.matches('[name="add"], .atc-button, .main-product-atc')
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      addProductToCart(form, event.submitter || form.querySelector('[name="add"]'));
    },
    true
  );
})();
